import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import { Calendar, Clock, SquareCheck, AlertCircle, Shield, Upload, Send, Check, Gift, Settings, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { parseUTC, formatDateLong } from '../../utils/date';
import { toast, confirmDialog } from '../../components/Toast/toast';
import { formatPrice } from '../../utils/price';

// Доступные тарифы в UI. 3/9 месяцев скрыты (2026-10-17) — бэкенд по-прежнему
// принимает их, чтобы старые подписки отображались в истории.
const TARIFF_DEFS = [
  { key: '1_month', label: '1 месяц', days: 30, settingsKey: null },
  { key: '6_months', label: '6 месяцев', days: 180, settingsKey: 'discount_6m' },
  { key: '12_months', label: '12 месяцев', days: 365, settingsKey: 'discount_12m' },
];

// Подписки, оформленные на скрытые тарифы, показываем по этим подписям
const LEGACY_TARIFF_LABELS = {
  '3_months': '3 месяца',
  '9_months': '9 месяцев',
};

const DEFAULT_SETTINGS = {
  subscription_price: 3990,
  discount_6m: 10,
  discount_12m: 20,
};

const tariffLabel = (key) =>
  TARIFF_DEFS.find(t => t.key === key)?.label
    || LEGACY_TARIFF_LABELS[key]
    || key;

function daysLeft(iso) {
  const end = parseUTC(iso);
  if (!end) return null;
  return Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
}

function calcPrice(months, pricePerMonth, discount) {
  const base = pricePerMonth * months;
  return Math.round(base * (1 - discount / 100));
}

function getTariffs(settings) {
  const price = settings.subscription_price || DEFAULT_SETTINGS.subscription_price;
  return TARIFF_DEFS.map(t => ({
    ...t,
    pricePerMonth: price,
    discount: t.settingsKey ? (settings[t.settingsKey] ?? DEFAULT_SETTINGS[t.settingsKey] ?? 0) : 0,
  }));
}

export default function SubscriptionAdmin() {
  const { user } = useApp();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Для разработчика
  const [selectedTariff, setSelectedTariff] = useState('1_month');
  const [extending, setExtending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editSettings, setEditSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Для владельца
  const [ownerTariff, setOwnerTariff] = useState('6_months');
  const [screenshot, setScreenshot] = useState(null);
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const fileRef = useRef(null);

  const isDeveloper = user?.is_developer || false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes, settingsRes] = await Promise.all([
        client.get('/subscription/status'),
        client.get('/subscription/').catch(() => ({ data: [] })),
        client.get('/settings/subscription').catch(() => ({ data: DEFAULT_SETTINGS })),
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data || []);
      setSettings(settingsRes.data || DEFAULT_SETTINGS);
    } catch (err) {
      console.error('Ошибка загрузки подписки:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // === Разработчик: продлить / отменить ===
  const handleExtend = async () => {
    setExtending(true);
    try {
      await client.post(`/subscription/extend?tariff=${selectedTariff}`);
      await load();
    } catch {
      toast.error('Ошибка продления подписки');
    } finally {
      setExtending(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirmDialog('Отменить активную подписку?'))) return;
    try {
      await client.post('/subscription/cancel');
      await load();
    } catch {
      toast.error('Ошибка отмены подписки');
    }
  };

  // === Владелец: загрузка скриншота ===
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setScreenshot(url);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message || 'Неизвестная ошибка';
      toast.error(`Ошибка загрузки (${status}): ${msg}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // === Владелец: отправить разработчику ===
  const handleSend = async () => {
    setSending(true);
    try {
      await client.post('/payment-request/', {
        tariff: ownerTariff,
        screenshot_url: screenshot || null,
        comment: comment.trim() || null,
      });
      setSent(true);
      setComment('');
      setScreenshot(null);
    } catch {
      toast.error('Ошибка отправки. Попробуйте ещё раз.');
    } finally {
      setSending(false);
    }
  };

  const remaining = status?.end_date ? daysLeft(status.end_date) : null;
  const isActive = remaining !== null && remaining > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-xl h-8 w-8 border-b-2 border-[#374151]"></div>
      </div>
    );
  }

  const tariffs = getTariffs(settings);
  const selectedPlan = tariffs.find(t => t.key === ownerTariff);
  const totalPrice = selectedPlan ? calcPrice(selectedPlan.days / 30, selectedPlan.pricePerMonth, selectedPlan.discount) : 0;
  const monthlyPrice = selectedPlan ? Math.round(totalPrice / (selectedPlan.days / 30)) : 0;
  const canSend = Boolean(screenshot || comment.trim());

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Текущий статус */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-5 w-5 text-[#4A6FA5]" />
          <h2 className="text-sm font-semibold text-[#0f172a]">Текущий статус</h2>
        </div>

        {status?.is_active ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#6B9B74]/20">
                <SquareCheck className="h-5 w-5 text-[#6B9B74]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0f172a]">Подписка активна</p>
                <p className="text-xs text-[#6B9B74]">Осталось {remaining} дн.</p>
              </div>
            </div>
            <div className="rounded-xl bg-[#f8fafc] p-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-[#64748b]">Тариф</span>
                <span className="font-medium text-[#0f172a]">{tariffLabel(status.tariff)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#64748b]">Действует до</span>
                <span className="font-medium text-[#0f172a]">{formatDateLong(status.end_date)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#D64545]/15">
              <AlertCircle className="h-5 w-5 text-[#D64545]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#0f172a]">Нет активной подписки</p>
              <p className="text-xs text-[#64748b]">Оформите подписку ниже</p>
            </div>
          </div>
        )}
      </div>

      {/* История подписок */}
      {history.length > 0 && (
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#4A6FA5]" />
              <h3 className="text-sm font-semibold text-[#0f172a]">История подписок</h3>
            </div>
            {history.length > 1 && (
              <button
                onClick={() => setHistoryExpanded(!historyExpanded)}
                className="flex items-center gap-1 text-xs font-medium text-[#374151]"
              >
                {historyExpanded ? 'Свернуть' : `Ещё ${history.length - 1}`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${historyExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
          <div className={`grid grid-cols-1 gap-3 [&>*]:min-w-0 ${(historyExpanded ? history : history.slice(0, 1)).length > 1 ? 'md:grid-cols-2' : ''}`}>
            {(historyExpanded ? history : history.slice(0, 1)).map((sub) => (
              <div key={sub.id} className="flex items-center justify-between rounded-xl bg-[#f8fafc] p-3">
                <div>
                  <p className="text-sm font-medium text-[#0f172a]">
                    {tariffLabel(sub.tariff)}
                  </p>
                  <p className="text-[10px] text-[#64748b]">
                    {formatDateLong(sub.start_date)} — {formatDateLong(sub.end_date)}
                  </p>
                </div>
                <span className={`rounded-xl px-2 py-0.5 text-[10px] font-medium ${
                  sub.is_active
                    ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
                    : 'bg-[#4A6FA5]/15 text-[#4A6FA5]'
                }`}>
                  {sub.is_active ? 'Активна' : 'Завершена'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === Разработчик: управление === */}
      {isDeveloper && (
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-[#4A6FA5]" />
            <h3 className="text-sm font-semibold text-[#0f172a]">Управление подпиской</h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[#64748b]">Тариф</label>
              <div className="grid grid-cols-2 gap-2">
                {tariffs.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTariff(key)}
                    className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                      selectedTariff === key
                        ? 'border-[#6B9B74] bg-[#6B9B74]/10 text-[#6B9B74]'
                        : 'border-[#e2e8f0] bg-white text-[#64748b]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleExtend}
              disabled={extending}
              className="w-full rounded-xl bg-[#374151] py-2.5 text-sm font-semibold text-white transition hover:bg-[#1f2937] active:scale-[0.98] disabled:opacity-50"
            >
              {extending ? 'Продление...' : 'Продлить подписку'}
            </button>

            {isActive && (
              <button
                onClick={handleCancel}
                className="w-full rounded-xl border border-[#D64545]/30 py-2.5 text-sm font-medium text-[#D64545] transition hover:bg-[#D64545]/5"
              >
                Отменить подписку
              </button>
            )}
          </div>
        </div>
      )}

      {/* === Разработчик: настройки подписки === */}
      {isDeveloper && (
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
          <button
            onClick={() => {
              if (!settingsOpen) setEditSettings({ ...settings });
              setSettingsOpen(!settingsOpen);
            }}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-[#374151]" />
              <h3 className="text-sm font-semibold text-[#0f172a]">Настройки цен</h3>
            </div>
            {settingsOpen ? <ChevronUp className="h-4 w-4 text-[#64748b]" /> : <ChevronDown className="h-4 w-4 text-[#64748b]" />}
          </button>

          {settingsOpen && editSettings && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Цена за месяц (₽)</label>
                <input
                  type="number"
                  value={editSettings.subscription_price || ''}
                  onChange={(e) => setEditSettings({ ...editSettings, subscription_price: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
                />
              </div>
              {[
                { key: 'discount_6m', label: 'Скидка 6 месяцев (%)' },
                { key: 'discount_12m', label: 'Скидка 12 месяцев (%)' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="mb-1 block text-xs font-medium text-[#64748b]">{label}</label>
                  <input
                    type="number"
                    value={editSettings[key] ?? ''}
                    onChange={(e) => setEditSettings({ ...editSettings, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
                  />
                </div>
              ))}
              <button
                onClick={async () => {
                  setSavingSettings(true);
                  try {
                    await client.put('/settings/subscription', editSettings);
                    setSettings(editSettings);
                    setSettingsOpen(false);
                  } catch {
                    toast.error('Ошибка сохранения настроек');
                  } finally {
                    setSavingSettings(false);
                  }
                }}
                disabled={savingSettings}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#374151] py-2.5 text-sm font-semibold text-white transition hover:bg-[#1f2937] active:scale-[0.98] disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {savingSettings ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* === Владелец: оформление подписки === */}
      {!isDeveloper && (
        <>
          {/* Что входит */}
          <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="h-5 w-5 text-[#E8A838]" />
              <h3 className="text-sm font-semibold text-[#0f172a]">Что входит в подписку</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#64748b]">
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-[#6B9B74] shrink-0" />
                <span>Техническое сопровождение магазина</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-[#6B9B74] shrink-0" />
                <span>Исправление багов и обновления</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-[#6B9B74] shrink-0" />
                <span>Хостинг и поддержка сервера</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-[#6B9B74] shrink-0" />
                <span>Консультации по настройке</span>
              </li>
            </ul>
          </div>

          {/* Выбор тарифа */}
          <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Выберите тариф</h3>
            <div className="space-y-2">
              {tariffs.map((tariff) => {
                const months = tariff.days / 30;
                const total = calcPrice(months, tariff.pricePerMonth, tariff.discount);
                const perMonth = Math.round(total / months);
                const isSelected = ownerTariff === tariff.key;

                return (
                  <button
                    key={tariff.key}
                    onClick={() => setOwnerTariff(tariff.key)}
                    className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition ${
                      isSelected
                        ? 'border-[#6B9B74] bg-[#6B9B74]/5 ring-1 ring-[#6B9B74]/30'
                        : 'border-[#e2e8f0] bg-white hover:bg-[#f8fafc]'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0f172a]">{tariff.label}</span>
                        {tariff.discount > 0 && (
                          <span className="rounded-xl bg-[#E8A838]/15 px-2 py-0.5 text-[10px] font-bold text-[#E8A838]">
                            −{tariff.discount}%
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-[#64748b]">{formatPrice(perMonth)} ₽/мес</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-[#0f172a]">{formatPrice(total)} ₽</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Скриншот оплаты */}
          <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Подтверждение оплаты</h3>

            {screenshot ? (
              <div className="relative mb-3">
                <img
                  src={screenshot}
                  alt="Скриншот"
                  className="w-full max-h-48 rounded-xl object-cover"
                />
                <button
                  onClick={() => setScreenshot(null)}
                  className="absolute top-2 right-2 grid h-6 w-6 place-items-center rounded-xl bg-black/50 text-white text-xs"
                >
                  ×
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#e2e8f0] p-6 cursor-pointer transition hover:border-[#374151]/50 hover:bg-[#374151]/5">
                <Upload className="h-8 w-8 text-[#64748b]" />
                <span className="text-sm text-[#64748b]">
                  {uploading ? 'Загрузка...' : 'Нажмите, чтобы прикрепить скриншот'}
                </span>
                <span className="text-[10px] text-[#64748b]/60">JPG, PNG до 10 МБ</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Комментарий (необязательно)"
              rows={2}
              className="mt-3 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder-[#64748b]/50 outline-none transition focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30 resize-none"
            />
          </div>

          {/* Кнопка отправки */}
          {sent ? (
            <div className="rounded-2xl bg-[#6B9B74]/10 p-4 text-center">
              <SquareCheck className="h-8 w-8 text-[#6B9B74] mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#0f172a]">Отправлено!</p>
              <p className="text-xs text-[#64748b] mt-1">Разработчик получил уведомление и продлит подписку в ближайшее время</p>
              <button
                onClick={() => setSent(false)}
                className="mt-3 text-xs font-medium text-[#6B9B74]"
              >
                Отправить ещё
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleSend}
                disabled={sending || uploading || !canSend}
                className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#374151] py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.98] disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {sending ? 'Отправка...' : `Отправить разработчику · ${formatPrice(totalPrice)} ₽`}
              </button>
              {!canSend && (
                <p className="mt-2 text-center text-xs text-[#64748b]">
                  Прикрепите скриншот оплаты или добавьте комментарий
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
